import { apiClient } from '@src/services/http-common'
import { LeadTimeRecord, LeadTimeRequest, LeadTimeResponse } from '@src/types/leadtime'
import { useQuery } from '@tanstack/react-query'

const LEADTIME_SCOPE = 'leadtime'

const keysFactory = {
  getLeadTime: (params: LeadTimeRequest) => [LEADTIME_SCOPE, params.startDate, params.endDate] as const,
}

// Raw async function
export async function getLeadTimeData(params: LeadTimeRequest): Promise<LeadTimeResponse> {
  const response = await apiClient.get<LeadTimeResponse>('/LeadTime', {
    params: {
      startDate: params.startDate,
      endDate: params.endDate,
    },
  })
  return response.data
}

// React Query hook
export function useGetLeadTimeData(params: LeadTimeRequest, enabled = true): [LeadTimeResponse | undefined, boolean] {
  const { isFetching, data } = useQuery({
    queryFn: () => getLeadTimeData(params),
    queryKey: keysFactory.getLeadTime(params),
    enabled: enabled && !!params.startDate && !!params.endDate,
  })
  return [data, isFetching]
}

// Client-side CSV export
export function exportLeadTimeToCsv(records: LeadTimeRecord[], startDate: string, endDate: string): void {
  const headers = [
    'ItemId',
    'ItemName',
    'ParentId',
    'ParentName',
    'ClosedDate',
    'MergedToDevDate',
    'MergedToMasterDate',
    'LeadTimeDays',
  ]

  const csvContent = [
    headers.join(','),
    ...records.map((record) =>
      [
        record.id,
        `"${record.workItemName.replace(/"/g, '""')}"`,
        record.parentId ?? '',
        record.parentName ? `"${record.parentName.replace(/"/g, '""')}"` : '',
        record.closedDate,
        record.mergedToDevDate ?? '',
        record.mergedToMasterDate,
        record.leadTimeDays,
      ].join(','),
    ),
  ].join('\n')

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `lead-time-${startDate}-to-${endDate}.csv`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
